import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  User,
  Calendar,
  Search,
  Trash2,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
} from "lucide-react";
import { conversationService } from "../../services/conversationService";
import { userDisplay } from "../../lib/conversationDisplay";
import { useAuth } from "../../contexts/AuthContext";
import type {
  Conversation,
  ConversationListResponse,
  ConversationListParams,
} from "../../types/conversation";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-brand-bg border-t border-brand-border sm:px-6">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-brand-text bg-brand-bg border border-brand-border rounded-md hover:bg-brand-surface disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-brand-text bg-brand-bg border border-brand-border rounded-md hover:bg-brand-surface disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-brand-text">
            Page <span className="font-medium">{currentPage + 1}</span> of{" "}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav
            className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            aria-label="Pagination"
          >
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-brand-muted bg-brand-bg border border-brand-border rounded-l-md hover:bg-brand-surface disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-brand-muted bg-brand-bg border border-brand-border rounded-r-md hover:bg-brand-surface disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

function ConversationRow({
  conversation,
  onView,
  onDelete,
}: {
  conversation: Conversation;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const formatDate = (dateString: string) => {
    // Ensure the date string is treated as UTC if it doesn't have timezone info
    const utcDateString = dateString.endsWith("Z")
      ? dateString
      : dateString + "Z";
    const date = new Date(utcDateString);
    return date.toLocaleString();
  };

  return (
    <tr className="hover:bg-brand-surface">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-brand-text">
        <div className="flex items-center">
          <MessageSquare className="w-4 h-4 mr-2 text-brand-muted" />
          {conversation.id.substring(0, 8)}...
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
        <div className="flex items-center" title={userDisplay(conversation).tooltip}>
          <User className="w-4 h-4 mr-2 text-brand-muted" />
          {userDisplay(conversation).label}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-brand-muted" />
          {formatDate(conversation.started_at)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
        {conversation.ended_at ? formatDate(conversation.ended_at) : "Active"}
      </td>
      <td className="px-6 py-4 text-sm text-brand-muted max-w-xs">
        <div
          className="truncate"
          title={conversation.first_message_snippet || "No message"}
        >
          {conversation.first_message_snippet || "No message"}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[rgba(72,178,205,0.1)] text-brand-teal">
          {conversation.message_count || 0} messages
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <ThumbsUp className="w-3 h-3 text-brand-teal" />
            <span className="text-brand-teal text-xs font-medium">
              {conversation.feedback_stats?.positive_count || 0}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ThumbsDown className="w-3 h-3 text-brand-error" />
            <span className="text-brand-error text-xs font-medium">
              {conversation.feedback_stats?.negative_count || 0}
            </span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => onView(conversation.id)}
            className="text-brand-primary hover:opacity-80 p-1 rounded hover:bg-brand-primary/5"
            title="View conversation"
          >
            <Eye className="w-4 h-4" />
          </button>
          {/* <button
            onClick={() => onDelete(conversation.id)}
            className="text-brand-error hover:text-brand-error p-1 rounded hover:bg-brand-error"
            title="Delete conversation"
          >
            <Trash2 className="w-4 h-4" />
          </button> */}
        </div>
      </td>
    </tr>
  );
}

export function ConversationsPage(): JSX.Element {
  const navigate = useNavigate();
  const { clerkUser } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [userIdFilter, setUserIdFilter] = useState("");
  const [filterUnanswered, setFilterUnanswered] = useState(false);
  const pageSize = 20;

  const fetchConversations = async (params: ConversationListParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await conversationService.getConversations({
        page: currentPage,
        size: pageSize,
        user_id: userIdFilter || undefined,
        has_unanswered: filterUnanswered ? true : undefined,
        ...params,
      });

      setConversations(response.conversations);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load conversations"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations({ page: currentPage });
  }, [currentPage, filterUnanswered]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchConversations({ page: 0 });
  };

  const handleViewConversation = (conversationId: string) => {
    navigate(`/admin/conversations/${conversationId}`);
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this conversation? This action cannot be undone."
      )
    ) {
      return;
    }

    if (!clerkUser) {
      alert("User not authenticated");
      return;
    }

    try {
      await conversationService.deleteConversation(conversationId);
      // Refresh the current page
      fetchConversations({ page: currentPage });
    } catch (err) {
      alert(
        err instanceof Error ? err.message : "Failed to delete conversation"
      );
    }
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-brand-muted">Loading conversations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-brand-text">Conversations</h1>
          <p className="mt-2 text-sm text-brand-text">
            A list of all conversations in the system ordered by creation date.
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-brand-bg shadow rounded-card">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="user-id"
                className="block text-sm font-medium text-brand-text"
              >
                Filter by user (name, email, or UUID)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="user-id"
                  value={userIdFilter}
                  onChange={(e) => setUserIdFilter(e.target.value)}
                  className="block w-full pr-10 border-brand-border rounded-md focus:ring-brand-primary focus:border-brand-primary"
                  placeholder="Enter a name, email, or user UUID..."
                />
              </div>
            </div>
            <div className="flex items-end gap-4">
              <button
                onClick={() => {
                  setFilterUnanswered(!filterUnanswered);
                  setCurrentPage(0);
                }}
                className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                  filterUnanswered
                    ? "bg-brand-warning/10 text-brand-warning border-brand-warning/30 hover:bg-brand-warning/20"
                    : "bg-brand-bg text-brand-text border-brand-border hover:bg-brand-surface"
                }`}
              >
                <AlertCircle className={`w-4 h-4 mr-2 ${filterUnanswered ? "text-brand-warning" : "text-brand-muted"}`} />
                Questions sans réponse
              </button>
              <button
                onClick={handleSearch}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-brand-error/10 border border-brand-error/30 rounded-md p-4">
          <div className="text-brand-error">{error}</div>
        </div>
      )}

      {/* Conversations Table */}
      <div className="bg-brand-bg shadow rounded-card overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-brand-border">
          <thead className="bg-brand-surface">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                Conversation ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                Started At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                Ended At
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                First Message
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                Messages
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
                Feedback
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-brand-muted uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-brand-bg divide-y divide-brand-border">
            {conversations.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-brand-muted">
                  No conversations found
                </td>
              </tr>
            ) : (
              conversations.map((conversation) => (
                <ConversationRow
                  key={conversation.id}
                  conversation={conversation}
                  onView={handleViewConversation}
                  onDelete={handleDeleteConversation}
                />
              ))
            )}
          </tbody>
        </table>

        {conversations.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Summary */}
      <div className="bg-brand-bg shadow rounded-card">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-sm text-brand-text">
            Showing {conversations.length} of {total} conversations
            {userIdFilter && ` (filtered by user ID: ${userIdFilter})`}
            {filterUnanswered && " (showing only conversations with unanswered questions)"}
          </div>
        </div>
      </div>
    </div>
  );
}
